import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

type Channel = Database['public']['Tables']['channels']['Row'];
type ChannelInsert = Database['public']['Tables']['channels']['Insert'];
type ChannelUpdate = Database['public']['Tables']['channels']['Update'];

export function useChannels(cohortId?: string | null) {
  return useQuery({
    queryKey: ['channels', cohortId],
    queryFn: async (): Promise<Channel[]> => {
      let query = supabase
        .from('channels')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');

      // If cohortId is explicitly null, get global channels
      // If cohortId is provided, get both global and cohort-specific channels
      if (cohortId === null) {
        query = query.is('cohort_id', null);
      } else if (cohortId) {
        query = query.or(`cohort_id.is.null,cohort_id.eq.${cohortId}`);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data || [];
    },
    staleTime: 1000 * 60 * 5, // 5 minutes - channels don't change often
  });
}

export function useAllChannels() {
  return useQuery({
    queryKey: ['channels', 'all'],
    queryFn: async (): Promise<Channel[]> => {
      const { data, error } = await supabase
        .from('channels')
        .select('*')
        .order('sort_order');

      if (error) throw error;
      return data || [];
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

export function useChannelBySlug(slug: string) {
  return useQuery({
    queryKey: ['channel', slug],
    queryFn: async (): Promise<Channel | null> => {
      const { data, error } = await supabase
        .from('channels')
        .select('*')
        .eq('slug', slug)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!slug,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

export function useCreateChannel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (channel: ChannelInsert) => {
      const { data, error } = await supabase
        .from('channels')
        .insert(channel)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channels'] });
    },
  });
}

export function useUpdateChannel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: ChannelUpdate & { id: string }) => {
      const { data, error } = await supabase
        .from('channels')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channels'] });
    },
  });
}

export function useDeleteChannel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('channels')
        .update({ is_active: false })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channels'] });
    },
  });
}
